import { ReactNode } from 'react';
import moment from 'moment';
import { Typography } from 'antd';
import {
    ICS2Notification,
    ICS2NotificationError,
    ics2TransactionTypeLabels,
    getIcs2NotificationMessageLabel,
} from 'store/declarations/eu/ics2/ics2-notification';
import useIcs2FunctionalErrorLabel from 'hooks/useIcs2FunctionalErrorLabel';
import DetailsHeader from 'views/declarations/common/ics2-message-details/DetailsHeader';
import MessageDetails from 'views/declarations/common/ics2-message-details/MessageDetails';
import { formatIcs2ErrorLocation } from 'views/declarations/eu/ics2/errors/formatLocation';
import { formatIcs2FieldPath } from 'views/declarations/eu/ics2/errors/formatFieldPath';
import { Ics2ErrorLocation } from 'views/declarations/eu/ics2/errors/types';
import useGoToIcs2ErrorLocation from 'views/declarations/eu/ics2/errors/useGoToIcs2ErrorLocation';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

type Props = {
    error: ICS2NotificationError;
    location?: Ics2ErrorLocation;
    notification?: ICS2Notification;
    onBack: () => void;
    nextSteps?: ReactNode;
    onClose?: () => void;
};

const Ics2ErrorDetails = ({ error, location: errorLocation, notification, onBack, nextSteps, onClose }: Props) => {
    const getIcs2FunctionalErrorLabel = useIcs2FunctionalErrorLabel();
    const goToErrorLocation = useGoToIcs2ErrorLocation(onClose);

    const message = notification?.notification;
    const messageElementPath = error.pointer?.messageElementPath;

    const fieldLabel = formatIcs2FieldPath(errorLocation?.fieldPath);

    const issuedAt = message?.notificationDate?.DateTime ?? notification?.createdAt;
    const messageTypeLabel = getIcs2NotificationMessageLabel(notification?.messageType);
    const locationLabel = formatIcs2ErrorLocation(errorLocation);

    const metadata: Record<string, ReactNode> = {
        Code: error.validationCode ?? '-',
        Message: getIcs2FunctionalErrorLabel(error.validationCode) ?? '-',
        ...(notification?.messageType
            ? { 'Message Type': `${notification.messageType}${messageTypeLabel ? ` - ${messageTypeLabel}` : ''}` }
            : {}),
        ...(message?.functionalReference ? { LRN: message.functionalReference } : {}),
        ...(message?.customsOfficeOfFirstEntry?.referenceNumber
            ? { 'Customs Office': message.customsOfficeOfFirstEntry.referenceNumber }
            : {}),
        ...(issuedAt ? { 'Issued At': moment(issuedAt).format(DATE_FORMAT) } : {}),
        // Customs rejects the exchange the error answers, which is not always the declaration itself.
        ...(notification?.ics2TransactionType
            ? { 'Applies To': ics2TransactionTypeLabels[notification.ics2TransactionType] }
            : {}),
        ...(locationLabel ? { Location: locationLabel } : {}),
        // Only a field we could place is worth offering as a link; the rest stay readable as text.
        ...(fieldLabel
            ? {
                  'Affected Field': (
                      <Typography.Link
                          style={{ fontWeight: 'normal' }}
                          onClick={() => goToErrorLocation(errorLocation)}
                      >
                          {fieldLabel}
                      </Typography.Link>
                  ),
              }
            : {}),
        // Kept verbatim so the mapping above can be checked against what customs actually sent.
        ...(messageElementPath ? { 'Message Element Path': messageElementPath } : {}),
    };

    return (
        <>
            <DetailsHeader onBack={onBack} />

            <MessageDetails
                messageMetadata={metadata}
                details={{
                    whatHappened:
                        error.description ??
                        getIcs2FunctionalErrorLabel(error.validationCode) ??
                        'Customs rejected the message without giving a description.',
                    // Only an IE3N99 carries one, and it names the element the message failed on.
                    whyItHappened: error.technicalErrorMessage ?? undefined,
                    nextSteps: nextSteps ?? (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            <li>
                                {fieldLabel && locationLabel
                                    ? `Correct ${fieldLabel} in ${locationLabel}.`
                                    : messageElementPath
                                    ? `Correct ${messageElementPath} in the declaration.`
                                    : 'Correct the reported data in the declaration.'}
                            </li>
                            <li>Resolve every error listed before resubmitting - customs reports them together.</li>
                            <li>Resubmit the ENS once the declaration is correct.</li>
                            <li>
                                Contact the customs office of first entry
                                {message?.customsOfficeOfFirstEntry?.referenceNumber
                                    ? ` (${message.customsOfficeOfFirstEntry.referenceNumber})`
                                    : ''}{' '}
                                if the error persists.
                            </li>
                        </ul>
                    ),
                }}
            />
        </>
    );
};

export default Ics2ErrorDetails;
